import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import './Profile.css';

const InputField = ({ label, value, onChange, type = 'text', disabled = false, readOnly = false, placeholder = '', options, hint = '' }) => (
  <label className="profile-input-label">
    <span className="profile-label-text">{label}</span>
    {options ? (
      <select value={value} onChange={onChange} disabled={disabled} aria-label={label}>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-label={label}
      />
    )}
    {hint && <span className="input-hint-text">{hint}</span>}
  </label>
);

const TokenProgress = ({ used, limit }) => {
  const percent = Math.min(100, (used / limit) * 100);
  return (
    <div className="token-progress" role="progressbar" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used}>
      <div className="bar" style={{ width: `${percent}%` }}></div>
      <span>
        {used} / {limit}
      </span>
    </div>
  );
};

const PersonalProfile = ({ personalInfo, setPersonalInfo, token, loading, setLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [editEmail, setEditEmail] = useState(personalInfo.email || '');

  useEffect(() => {
    setEditEmail(personalInfo.email || '');
  }, [personalInfo.email]);

  const updatePersonalProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/dashboard/me`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: editEmail }),
      });
      if (!res.ok) throw new Error('Failed to update');
      const data = await res.json();
      setPersonalInfo((prev) => ({ ...prev, email: data.email || editEmail }));
      setMessage('✅ Personal profile updated.');
      setIsEditing(false);
    } catch {
      setMessage('❌ Personal profile update failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="profile-section" aria-labelledby="personal-profile-title">
      <div className="profile-section-header">
        <h3 id="personal-profile-title">👤 Personal &amp; Account Identity</h3>
        <span className="profile-badge-identity">Tenant Account</span>
      </div>

      <div className="plan-summary-banner">
        <div className="plan-summary-top">
          <div>
            <span className="plan-label-small">Active Subscription Tier</span>
            <div className="plan-value-large">{personalInfo.plan || 'Free Plan'}</div>
          </div>
          <Link to="/billing" className="btn-manage-plan">
            💳 Manage / Upgrade Plan &rarr;
          </Link>
        </div>
        <p className="plan-locked-notice">
          🔒 <strong>Plan &amp; Quotas are Locked:</strong> Plan details, monthly processing allowances, and token caps cannot be modified directly in user profile settings. To upgrade your tier or request burst capacity, visit Billing.
        </p>

        <div className="plan-quota-stats">
          <div className="quota-stat-item">
            <span className="stat-label">Monthly Limit:</span>
            <span className="stat-value">{(personalInfo.tokenLimitPerMonth || 0).toLocaleString()} tokens</span>
          </div>
          <div className="quota-stat-item">
            <span className="stat-label">Tokens Used This Month:</span>
            <span className="stat-value">{(personalInfo.tokensUsedThisMonth || 0).toLocaleString()} tokens</span>
          </div>
        </div>

        {personalInfo.tokenLimitPerMonth > 0 && (
          <TokenProgress used={personalInfo.tokensUsedThisMonth} limit={personalInfo.tokenLimitPerMonth} />
        )}
      </div>

      {!isEditing ? (
        <div className="personal-details-display">
          <p><strong>Login Email:</strong> {personalInfo.email}</p>
          <p><strong>Tenant UID:</strong> <code>{personalInfo.uid}</code></p>
          <p><strong>System Role:</strong> <span className="role-tag">{personalInfo.role}</span></p>

          <button onClick={() => setIsEditing(true)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            ✏️ Edit Account Email
          </button>
        </div>
      ) : (
        <form onSubmit={updatePersonalProfile} className="profile-form" noValidate style={{ marginTop: '1rem' }}>
          <InputField
            label="Login / Contact Email"
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            hint="Used for system alerts, audit notifications, and account recovery"
          />
          <InputField label="Tenant UID (Read-only)" value={personalInfo.uid} disabled readOnly />
          <InputField label="Assigned Role (Read-only)" value={personalInfo.role} disabled readOnly />

          <div className="profile-buttons">
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : 'Save Email'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </button>
          </div>
          {message && <p className={`message ${message.startsWith('✅') ? 'success' : 'error'}`}>{message}</p>}
        </form>
      )}
    </section>
  );
};

const CorporateProfile = ({ corpProfile, setCorpProfile, token, loading, setLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');

  const updateCorporateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/corporate-profile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(corpProfile),
      });
      if (!res.ok) throw new Error('Failed to update');
      const data = await res.json();
      setCorpProfile(data);
      setMessage('✅ Corporate profile updated.');
      setIsEditing(false);
    } catch {
      setMessage('❌ Update failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="profile-section" aria-labelledby="corporate-profile-title">
      <div className="profile-section-header">
        <h3 id="corporate-profile-title">🏢 Corporate Profile &amp; Legal Entity</h3>
        <span className="profile-badge-identity editable">Fully Editable</span>
      </div>
      <p className="section-description-text">
        Enter your enterprise organization and legal entity details. These credentials are used for compliance audit packets, security whitepaper records, and monthly tax invoicing.
      </p>

      {!isEditing ? (
        <>
          <div className="readonly-fields">
            {Object.entries(corpProfile).map(([key, value]) => {
              if (!value) return null;
              if (key === 'logoUrl') {
                return (
                  <p key={key}>
                    <strong>{formatLabel(key)}:</strong>{' '}
                    <a href={value} target="_blank" rel="noopener noreferrer">
                      {value}
                    </a>
                  </p>
                );
              }
              return (
                <p key={key}>
                  <strong>{formatLabel(key)}:</strong> {value}
                </p>
              );
            })}
          </div>
          <button onClick={() => setIsEditing(true)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            ✏️ Edit Corporate Profile
          </button>
        </>
      ) : (
        <form onSubmit={updateCorporateProfile} className="profile-form" noValidate style={{ marginTop: '1rem' }}>
          <InputField
            label="Company Legal Name"
            value={corpProfile.companyName}
            placeholder="e.g. Acme Global Technologies Inc."
            hint="Official legal name of the organization"
            onChange={(e) => setCorpProfile({ ...corpProfile, companyName: e.target.value })}
          />
          <InputField
            label="Company Official Email"
            type="email"
            value={corpProfile.companyEmail}
            placeholder="e.g. security@acmeglobal.com"
            hint="Primary address for security and billing notifications"
            onChange={(e) => setCorpProfile({ ...corpProfile, companyEmail: e.target.value })}
          />
          <InputField
            label="Company Phone"
            value={corpProfile.companyPhone}
            placeholder="e.g. +1 (415) 555-0199"
            hint="Headquarters or SOC emergency hotline"
            onChange={(e) => setCorpProfile({ ...corpProfile, companyPhone: e.target.value })}
          />
          <InputField
            label="Tax ID / VAT / GSTIN"
            value={corpProfile.taxId}
            placeholder="e.g. US-EIN-12-3456789 or 27AAAAA0000A1Z5"
            hint="Tax identifier for commercial invoices and vendor records"
            onChange={(e) => setCorpProfile({ ...corpProfile, taxId: e.target.value })}
          />
          <InputField
            label="Registered Address"
            value={corpProfile.address}
            placeholder="e.g. 100 Enterprise Way, Suite 400"
            hint="Headquarters or registered corporate address"
            onChange={(e) => setCorpProfile({ ...corpProfile, address: e.target.value })}
          />
          <InputField
            label="City"
            value={corpProfile.city}
            placeholder="e.g. San Francisco / Bengaluru"
            onChange={(e) => setCorpProfile({ ...corpProfile, city: e.target.value })}
          />
          <InputField
            label="State / Province"
            value={corpProfile.state}
            placeholder="e.g. California / Karnataka"
            onChange={(e) => setCorpProfile({ ...corpProfile, state: e.target.value })}
          />
          <InputField
            label="Country"
            value={corpProfile.country}
            placeholder="e.g. United States / India"
            onChange={(e) => setCorpProfile({ ...corpProfile, country: e.target.value })}
          />
          <InputField
            label="Zip / Postal Code"
            value={corpProfile.zipCode}
            placeholder="e.g. 94107 / 560001"
            onChange={(e) => setCorpProfile({ ...corpProfile, zipCode: e.target.value })}
          />
          <InputField
            label="Company Logo URL"
            type="url"
            value={corpProfile.logoUrl}
            placeholder="e.g. https://acme.com/assets/logo.png"
            hint="Public HTTPS link to transparent SVG or PNG logo"
            onChange={(e) => setCorpProfile({ ...corpProfile, logoUrl: e.target.value })}
          />

          <div className="profile-buttons">
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving Corporate Info...' : 'Save Corporate Profile'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </button>
          </div>
          {message && <p className={`message ${message.startsWith('✅') ? 'success' : 'error'}`}>{message}</p>}
        </form>
      )}
    </section>
  );
};

const formatLabel = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

const Profile = () => {
  const { token } = useAuth();
  const [corpProfile, setCorpProfile] = useState({ companyName:'', companyEmail:'', companyPhone:'', address:'', city:'', state:'', country:'', zipCode:'', logoUrl:'', taxId:'' });
  const [personalInfo, setPersonalInfo] = useState({ uid:'', email:'', role:'', plan:'', tokenLimitPerMonth:0, tokensUsedThisMonth:0 });
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [corpForbidden, setCorpForbidden] = useState(false);


  useEffect(() => {
  if (!token) return;

  const fetchPersonal = async () => {
    try {
      const res = await fetch(`${API_BASE}/dashboard/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 403) {
        setAuthError('You are not authorized to access this page.');
        return;
      }

      if (res.ok) {
        setPersonalInfo(await res.json());
      }
    } catch {
      setAuthError('Failed to verify permissions.');
    }
  };

  const fetchCorporate = async () => {
    try {
      const res = await fetch(`${API_BASE}/corporate-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 403) {
        setCorpForbidden(true);
        return;
      }

      if (res.ok) {
        setCorpProfile(await res.json());
      }
    } catch {
      setCorpForbidden(true);
    }
  };

  fetchPersonal();
  fetchCorporate();
}, [token]);


  if (authError) {
    return (
      <div className="profile-container">
        <div className="permission-error">
          <h2>Access Denied</h2>
          <p>{authError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <h2>👤 Profile</h2>
      <PersonalProfile
        personalInfo={personalInfo}
        setPersonalInfo={setPersonalInfo}
        token={token}
        loading={loading}
        setLoading={setLoading}
      />
     {!corpForbidden ? (
  <CorporateProfile
    corpProfile={corpProfile}
    setCorpProfile={setCorpProfile}
    token={token}
    loading={loading}
    setLoading={setLoading}
  />
) : (
  <div className="profile-section permission-error">
    <h3>🏢 Corporate Profile</h3>
    <p>You do not have permission to view or edit corporate details.</p>
  </div>
)}

    </div>
  );
};

export default Profile;
